import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { toChargeListResponse } from '@app/data/mappers/payments/charge-list.mapper';
import { mapCompany, toCompanyListResponse } from '@app/data/mappers/payments/company-list.mapper';

import { environment } from 'src/environments/environment';
import { BrowserStorageService } from '@app/services/browser-storage.service';
import { EncryptionService } from '@app/services/encryption.service';
import { PolipayPaymentsRepository } from '@app/domain/ports/polipay-payments.repository';
import { newIdempotencyKey } from '@app/shared/utils/idempotency-key.util';
import {
  EncryptionKeyResponse,
  PaymentChargeCreateRequest,
  PaymentCompany,
  PaymentCompanyCreateRequest,
  PaymentCompanyDto,
  PaymentCompanyListParams,
  PaymentCompanyListResponse,
  PaymentChargeListParams,
  PaymentChargeListResponse,
  PaymentChargeResponse,
  PaymentLinkCreateRequest,
  PaymentLinkListParams,
  PaymentLinkListResponse,
  PaymentLinkResponse,
  PaymentLinkSendRequest,
  PaymentLinkSendResponse,
  PaymentSummaryGlobalResponse,
  PaymentSummaryQueryParams,
  PaymentSummaryResponse,
} from '@app/shared/models/polipay-payment.model';

/**
 * Data source remoto del contrato Polipay Payments (`PAYMENTS_FRONT_CONTRATO.md`),
 * base `${apiUrl}/api/v1/payments/`. La suite es el BFF: el front habla SOLO con
 * estos endpoints, nunca directo con la pasarela. Implementa `PolipayPaymentsRepository`.
 *
 * Auth: JWT Bearer del token de sesión (`sessionStorage.access`). Se fija
 * explícitamente en `buildJsonHeaders()` porque en algunos entornos el
 * `authInterceptor` global no llega a poblar el header a tiempo — así el
 * request al menos sale con el token conocido del cliente.
 *
 * Convenciones:
 *  - `Idempotency-Key` se envía SIEMPRE en `POST /links/` y `POST /charges/`;
 *    el consumidor puede pasar uno propio para reintentos idempotentes.
 *  - Nunca se manda `card`/`cardNumber`/`cvv` en claro: usar
 *    `PolipayPaymentsEncryptionService.encryptCard()` antes de armar el body.
 */
@Injectable()
export class PolipayPaymentsRemoteDataSource extends PolipayPaymentsRepository {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(BrowserStorageService);
  private readonly encryption = inject(EncryptionService);

  private readonly baseUrl = `${environment.apiUrl}/api/v1/payments`;

  // ── Links de cobro ──────────────────────────────────────────────

  createLink(input: PaymentLinkCreateRequest, idempotencyKey?: string): Observable<PaymentLinkResponse> {
    const headers = this.buildJsonHeaders(idempotencyKey ?? newIdempotencyKey());
    return this.http.post<PaymentLinkResponse>(`${this.baseUrl}/links/`, input, { headers });
  }

  listLinks(params: PaymentLinkListParams = {}): Observable<PaymentLinkListResponse> {
    const httpParams = this.buildLinkListParams(params);
    return this.http.get<PaymentLinkListResponse>(`${this.baseUrl}/links/`, {
      headers: this.buildJsonHeaders(),
      params: httpParams,
    });
  }

  /**
   * Envía el link al cliente por email o SMS
   * (§`POST /links/{id_link}/send/`). Solo aplica a links `Pending`; el
   * backend responde `409` si el link ya está pagado/expirado, `503` si el
   * canal `sms` no está configurado en el ambiente (Twilio) y `502` si el
   * proveedor de envío falla.
   *
   * `idLink` es el ENTERO local (`PaymentLinkResponse.id_link`), no el UUID
   * del gateway (`PaymentLinkResponse.id`).
   */
  sendLink(idLink: number, input: PaymentLinkSendRequest): Observable<PaymentLinkSendResponse> {
    const url = `${this.baseUrl}/links/${encodeURIComponent(String(idLink))}/send/`;
    return this.http.post<PaymentLinkSendResponse>(url, input, {
      headers: this.buildJsonHeaders(),
    });
  }

  /**
   * Cancela un link vigente (§`POST /links/{id_link}/cancel/`, contrato
   * 2026-07-21). El endpoint no consume body y es idempotente: si el link ya
   * estaba cancelado, sigue devolviendo `200`. Errores relevantes:
   *  - `409 {code: "LINK_ALREADY_PAID"}` — se pagó justo antes; procede devolución
   *  - `409 {code: "LINK_NOT_CANCELABLE"}` — ya expiró
   *  - `404` — no existe
   *  - `502` — gateway no respondió (reintenta con la misma llamada)
   */
  cancelLink(idLink: number): Observable<PaymentLinkResponse> {
    const url = `${this.baseUrl}/links/${encodeURIComponent(String(idLink))}/cancel/`;
    return this.http.post<PaymentLinkResponse>(url, {}, {
      headers: this.buildJsonHeaders(),
    });
  }

  /**
   * Soft-delete de un link (§`DELETE /links/{id_link}/`, contrato 2026-07-21).
   * Solo lo oculta del listado por defecto (`?include_deleted=1` lo trae de
   * vuelta para auditoría). Idempotente. Errores relevantes:
   *  - `409 {code: "LINK_CANCEL_FIRST"}` — el link sigue `Pending`; hay que
   *    cancelarlo primero. No deberíamos llegar aquí porque el botón solo se
   *    muestra en estados terminales, pero lo mapeamos como defensa.
   *  - `404` — no existe
   * `summary/` NO excluye eliminados (contrato §Números del tenant: son
   * verdad de dinero).
   */
  deleteLink(idLink: number): Observable<void> {
    const url = `${this.baseUrl}/links/${encodeURIComponent(String(idLink))}/`;
    return this.http.delete<void>(url, {
      headers: this.buildJsonHeaders(),
    });
  }

  // ── Summary por tenant ──────────────────────────────────────────

  getSummary(params?: PaymentSummaryQueryParams): Observable<PaymentSummaryResponse> {
    return this.http.get<PaymentSummaryResponse>(`${this.baseUrl}/summary/`, {
      headers: this.buildJsonHeaders(),
      params: this.buildSummaryParams(params),
    });
  }

  getSummaryGlobal(
    params?: Pick<PaymentSummaryQueryParams, 'kind' | 'date_from' | 'date_to'>,
  ): Observable<PaymentSummaryGlobalResponse> {
    return this.http.get<PaymentSummaryGlobalResponse>(`${this.baseUrl}/summary/`, {
      headers: this.buildJsonHeaders(),
      params: this.buildSummaryParams(params),
    });
  }

  // ── Encryption key + Cargo directo ──────────────────────────────

  /**
   * Consumo directo desde `PolipayPaymentsEncryptionService`, que lo cachea.
   * El componente no debería llamarlo directamente en la mayoría de casos.
   */
  getEncryptionKey(): Observable<EncryptionKeyResponse> {
    return this.http.get<EncryptionKeyResponse>(`${this.baseUrl}/encryption-key/`, {
      headers: this.buildJsonHeaders(),
    });
  }

  createCharge(input: PaymentChargeCreateRequest, idempotencyKey?: string): Observable<PaymentChargeResponse> {
    const headers = this.buildJsonHeaders(idempotencyKey ?? newIdempotencyKey());
    return this.http.post<PaymentChargeResponse>(`${this.baseUrl}/charges/`, input, { headers });
  }

  /**
   * Confirma el estado final del pago tras el challenge 3DS.
   * El CVV se cachea ~10 min upstream: si el cliente tarda más, el pago pasa a `Failed`.
   */
  verify3ds(paymentId: string): Observable<PaymentChargeResponse> {
    const url = `${this.baseUrl}/charges/${encodeURIComponent(paymentId)}/verify-3ds/`;
    return this.http.post<PaymentChargeResponse>(url, {}, { headers: this.buildJsonHeaders() });
  }

  /**
   * `GET /charges/` — espejo local de cargos (§Movimientos por cliente).
   *
   * `id_company` NO es opcional en la práctica para un operador acotado: sin él el
   * backend responde **403**, no lista vacía. Lo manda quien llama, igual que en
   * `/links/`: este data source no conoce el contexto de empresa.
   */
  listCharges(params: PaymentChargeListParams = {}): Observable<PaymentChargeListResponse> {
    return this.http
      .get<unknown>(`${this.baseUrl}/charges/`, {
        headers: this.buildJsonHeaders(),
        params: this.buildChargeListParams(params),
      })
      .pipe(map(toChargeListResponse));
  }

  // ── Comercios (§4 sub-sección) ──────────────────────────────────

  /**
   * `GET /api/v1/payments/companies/` — lista los comercios del alcance del usuario.
   * Incluye `cobra_a` y `gateway_account_id` para detectar los sin destino configurado.
   * `params.search` se pasa al backend para búsqueda server-side.
   */
  listCompanies(params: PaymentCompanyListParams = {}): Observable<PaymentCompanyListResponse> {
    return this.http
      .get<unknown>(`${this.baseUrl}/companies/`, {
        headers: this.buildJsonHeaders(),
        params: this.buildCompaniesListParams(params),
      })
      .pipe(map(toCompanyListResponse));
  }

  /**
   * `POST /api/v1/payments/companies/` — da de alta un comercio (contrato 2026-10-05).
   * El cuerpo viaja TAL CUAL (incluye `totp_code`, que no se registra en ningún lado).
   * El 201 tiene la forma de un renglón de `GET /companies/`, así que se mapea con el
   * MISMO `mapCompany` del listado: el alta y la tabla describen igual al comercio
   * (incluido su `cobra_a`). Los errores pasan intactos: la pantalla los clasifica.
   */
  createCompany(input: PaymentCompanyCreateRequest): Observable<PaymentCompany> {
    return this.http
      .post<unknown>(`${this.baseUrl}/companies/`, input, { headers: this.buildJsonHeaders() })
      .pipe(map((raw) => mapCompany((raw ?? {}) as PaymentCompanyDto)));
  }

  // ── Helpers ─────────────────────────────────────────────────────

  /**
   * Query de `GET /summary/` (§Summary/Filtros). Sirve a `getSummary` y a
   * `getSummaryGlobal` — comparten endpoint y solo cambia el shape de la
   * respuesta según si `id_company` viene o no. Solo se envían los campos con
   * valor; el resto queda al default del backend (que excluye PREAUTH).
   */
  private buildSummaryParams(params?: PaymentSummaryQueryParams): HttpParams {
    let p = new HttpParams();
    if (
      params?.id_company !== undefined &&
      params.id_company !== null &&
      String(params.id_company) !== ''
    ) {
      p = p.set('id_company', String(params.id_company));
    }
    if (params?.kind) {
      p = p.set('kind', params.kind);
    }
    // El rango solo viaja si trae valor: las llamadas sin él quedan idénticas a antes.
    if (params?.date_from) {
      p = p.set('date_from', params.date_from);
    }
    if (params?.date_to) {
      p = p.set('date_to', params.date_to);
    }
    return p;
  }

  private buildLinkListParams(params: PaymentLinkListParams): HttpParams {
    let p = new HttpParams();
    if (params.id_company !== undefined && params.id_company !== null && params.id_company !== '') {
      p = p.set('id_company', String(params.id_company));
    }
    if (params.status) {
      // `params.status` es `PaymentLinkStatusFilter` (union con `'Deleted'`
      // como pseudo-valor de filtro). `HttpParams.set` acepta `string`, así
      // que la unión se serializa tal cual sin cast.
      p = p.set('status', params.status);
    }
    if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
      // El backend acota internamente a [1, 200], pero clampamos para no mandar valores absurdos.
      const clamped = Math.max(1, Math.min(200, Math.trunc(params.limit)));
      p = p.set('limit', String(clamped));
    }
    if (params.search && params.search.trim()) {
      p = p.set('search', params.search.trim());
    }
    if (params.date_from) {
      p = p.set('date_from', params.date_from);
    }
    if (params.date_to) {
      p = p.set('date_to', params.date_to);
    }
    if (params.min_amount) {
      p = p.set('min_amount', params.min_amount);
    }
    if (params.max_amount) {
      p = p.set('max_amount', params.max_amount);
    }
    // §Cambios Payments 2026-07-21: `?include_deleted=1` opt-in para vista
    // de auditoría. Solo se manda cuando el caller lo pide explícitamente
    // (`=== true`) — un `undefined`/`false` NO agrega el param, respetando
    // el default del backend (solo visibles).
    if (params.include_deleted === true) {
      p = p.set('include_deleted', '1');
    }
    if (typeof params.offset === 'number' && Number.isFinite(params.offset) && params.offset > 0) {
      p = p.set('offset', String(Math.trunc(params.offset)));
    }
    return p;
  }

  /**
   * Query de `GET /charges/`. Espeja `buildLinkListParams`: nada se manda
   * incondicionalmente y el `limit` se acota a [1, 200] en cliente (el backend lo
   * acota igual, pero no tiene sentido pedirle un absurdo).
   * Contrato §2 (2026-09-22): 7 params nuevos en orden status, search, date_from,
   * date_to, min_amount, max_amount, offset.
   */
  private buildChargeListParams(params: PaymentChargeListParams): HttpParams {
    let p = new HttpParams();
    if (params.id_company !== undefined && params.id_company !== null && params.id_company !== '') {
      p = p.set('id_company', String(params.id_company));
    }
    if (params.customer_email && params.customer_email.trim()) {
      p = p.set('customer_email', params.customer_email.trim());
    }
    if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
      const clamped = Math.max(1, Math.min(200, Math.trunc(params.limit)));
      p = p.set('limit', String(clamped));
    }
    if (params.kind) {
      p = p.set('kind', params.kind);
    }
    if (params.status) {
      p = p.set('status', params.status);
    }
    if (params.search && params.search.trim()) {
      p = p.set('search', params.search.trim());
    }
    if (params.date_from) {
      p = p.set('date_from', params.date_from);
    }
    if (params.date_to) {
      p = p.set('date_to', params.date_to);
    }
    if (params.min_amount) {
      p = p.set('min_amount', params.min_amount);
    }
    if (params.max_amount) {
      p = p.set('max_amount', params.max_amount);
    }
    if (typeof params.offset === 'number' && Number.isFinite(params.offset) && params.offset > 0) {
      p = p.set('offset', String(Math.trunc(params.offset)));
    }
    return p;
  }

  /**
   * Query de `GET /companies/`. Espeja `buildChargeListParams`/`buildLinkListParams`:
   * nada se manda incondicionalmente y `limit` se acota a [1, 200] en cliente
   * (el backend lo acota igual, pero pedirle un absurdo no tiene sentido).
   * Contrato §Companies rev. 2026-09-25: los 4 params server-side son
   * `search`, `is_active`, `cobra_a`, `limit`, `offset`.
   */
  private buildCompaniesListParams(params: PaymentCompanyListParams): HttpParams {
    let p = new HttpParams();
    const search = params.search?.trim();
    if (search) {
      p = p.set('search', search);
    }
    if (typeof params.is_active === 'boolean') {
      p = p.set('is_active', String(params.is_active));
    }
    const cobraA = params.cobra_a?.trim();
    if (cobraA) {
      p = p.set('cobra_a', cobraA);
    }
    if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
      const clamped = Math.min(200, Math.max(1, Math.trunc(params.limit)));
      p = p.set('limit', String(clamped));
    }
    if (typeof params.offset === 'number' && Number.isFinite(params.offset) && params.offset > 0) {
      p = p.set('offset', String(Math.trunc(params.offset)));
    }
    return p;
  }

  private buildJsonHeaders(idempotencyKey?: string): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    // Fijamos `Authorization: Bearer <access>` explícitamente. El `authInterceptor`
    // global también intentará ponerlo con `setHeaders`, pero preferimos garantizar
    // el header aquí para que el POST /links/ salga autenticado incluso si el
    // interceptor no llega a correr en algún entorno.
    const token = this.readAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    // Contrato: "Manda `Idempotency-Key` en POST /links/ y POST /charges/". Auto-generar UUID
    // cuando el caller no pase uno; permitir reintentos idempotentes reutilizando el mismo valor.
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }
    return headers;
  }

  /**
   * Lee el `access` cifrado del `sessionStorage`, lo desencripta y lo devuelve
   * como string plano. Devuelve cadena vacía si no existe o si la
   * desencriptación falla — el request saldrá sin `Authorization` y el
   * backend responderá 401 explícito en vez de romperse aquí.
   */
  private readAccessToken(): string {
    const encrypted = this.storage.getSessionStorageItem('access');
    if (!encrypted) return '';
    try {
      return this.encryption.decrypt(encrypted) || '';
    } catch {
      return '';
    }
  }
}
